<template>
  <div class="login-page">
    <!-- Google Sign-in Details Modal - Using v-if for direct control instead of Bootstrap's show/hide -->
    <div v-if="showGoogleModal" class="modal-backdrop fade show"></div>
    <div v-if="showGoogleModal" class="modal fade show" id="googleSignInModal" tabindex="-1" aria-labelledby="googleSignInModalLabel" style="display: block;" aria-modal="true" role="dialog">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="googleSignInModalLabel">{{ translate('auth.complete_profile', 'Complete Your Profile') }}</h5>
            <button type="button" class="btn-close" @click="hideGoogleModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="$store.state.error" class="alert alert-danger mb-3">
              {{ $store.state.error }}
            </div>
            <form @submit.prevent="submitGoogleUserDetails">
              <div class="mb-3">
                <label class="form-label">{{ translate('auth.store_name', 'Store Name') }}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  :placeholder="translate('auth.store_name', 'Enter your store name')"
                  v-model="googleUserDetails.storeName"
                  required
                  autocomplete="organization"
                >
              </div>
              
              <div class="mb-3">
                <label class="form-label">{{ translate('auth.phone_number', 'Phone Number') }}</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  :placeholder="translate('auth.phone_number', 'Enter your phone number')"
                  v-model="googleUserDetails.phoneNumber"
                  required
                  autocomplete="tel"
                >
              </div>
              
              <div class="d-grid">
                <button type="submit" class="btn btn-primary" :disabled="googleDetailsLoading">
                  <span v-if="googleDetailsLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ translate('auth.save', 'Save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Registration Details Modal - For email/password registration -->
    <div v-if="showRegistrationModal" class="modal-backdrop fade show"></div>
    <div v-if="showRegistrationModal" class="modal fade show" id="registrationModal" tabindex="-1" aria-labelledby="registrationModalLabel" style="display: block;" aria-modal="true" role="dialog">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="registrationModalLabel">{{ translate('auth.complete_profile', 'Complete Your Profile') }}</h5>
            <button type="button" class="btn-close" @click="hideRegistrationModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="$store.state.error" class="alert alert-danger mb-3">
              {{ $store.state.error }}
            </div>
            <form @submit.prevent="submitRegistrationDetails">
              <div class="mb-3">
                <label class="form-label">{{ translate('auth.store_name', 'Store Name') }}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  :placeholder="translate('auth.store_name', 'Enter your store name')"
                  v-model="registrationDetails.storeName"
                  required
                  autocomplete="organization"
                >
              </div>
              
              <div class="mb-3">
                <label class="form-label">{{ translate('auth.phone_number', 'Phone Number') }}</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  :placeholder="translate('auth.phone_number', 'Enter your phone number')"
                  v-model="registrationDetails.phoneNumber"
                  required
                  autocomplete="tel"
                >
              </div>
              
              <div class="d-grid">
                <button type="submit" class="btn btn-primary" :disabled="registrationLoading">
                  <span v-if="registrationLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ translate('auth.save', 'Save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Terms and Conditions Modal Component -->
    <div class="login-container">
      <TermsAndConditionsModal v-model="showTermsAndConditionsModal" :show-privacy-section="showPrivacySection" :show-terms-section="!showPrivacySection" @accepted="acceptTerms = true" />
    </div>

    <div class="container">
      <div class="row justify-content-center align-items-center min-vh-100">
        <div class="col-sm-12 col-md-10 col-lg-8">
          <div class="card shadow-lg">
            <div class="card-body p-5">
              <div class="row">
                <div class="col-md-6 text-center mb-4">
                  <img src="@/assets/logotechnopaws.png" alt="PawPulse" class="mb-4" style="max-width: 200px;">
                  <h2 class="fw-bold" style="color: var(--primary-blue);">PawPulse</h2>
                  <p>{{ translate('auth.description', 'Manage your pet business with ease') }}</p>
                </div>
                <div class="col-md-6">
                  <!-- Bottons of tabs to alternate between registration and login -->
                  <div class="auth-tabs mb-4">
                    <button 
                      type="button" 
                      class="tab-button" 
                      :class="{'active': !isRegistering}" 
                      @click="isRegistering = false"
                    >
                      {{ translate('auth.sign_in', 'Sign In') }}
                    </button>
                    <button 
                      type="button" 
                      class="tab-button" 
                      :class="{'active': isRegistering}" 
                      @click="isRegistering = true"
                    >
                      {{ translate('auth.register', 'Create Account') }}
                    </button>
                  </div>
                  <form @submit.prevent="handleSubmit">
                    <div class="mb-3">
                      <label class="form-label">{{ translate('auth.email', 'Email address') }}</label>
                      <input 
                        type="email" 
                        class="form-control form-control-lg" 
                        :placeholder="translate('auth.email', 'Enter your email')"
                        v-model="email"
                        required
                      >
                    </div>
                    
                    <!-- Store name and phone fields (only shown during registration) -->
                    <template v-if="false">
                      <div class="mb-3">
                        <label class="form-label">{{ translate('auth.store_name', 'Store Name') }}</label>
                        <input 
                          type="text" 
                          class="form-control form-control-lg" 
                          :placeholder="translate('auth.store_name', 'Enter your store name')"
                          v-model="storeName"
                          required
                        >
                      </div>
                      
                      <div class="mb-3">
                        <label class="form-label">{{ translate('auth.phone_number', 'Phone Number') }}</label>
                        <input 
                          type="tel" 
                          class="form-control form-control-lg" 
                          :placeholder="translate('auth.phone_number', 'Enter your phone number')"
                          v-model="phoneNumber"
                          required
                        >
                      </div>
                      
                      <div class="mb-3">
                        <label class="form-label">{{ translate('auth.language_selection', 'Preferred Language') }} ({{ translate('auth.language_permanent', 'cannot be changed later') }})</label>
                        <div class="language-options">
                          <div 
                            class="language-option" 
                            :class="{ selected: language === 'en' }"
                            @click="language = 'en'"
                          >
                            <div class="language-flag">🇺🇸</div>
                            <div class="language-name">{{ translate('auth.english', 'English') }}</div>
                          </div>
                          
                          <div 
                            class="language-option" 
                            :class="{ selected: language === 'es' }"
                            @click="language = 'es'"
                          >
                            <div class="language-flag">🇪🇸</div>
                            <div class="language-name">{{ translate('auth.spanish', 'Español') }}</div>
                          </div>
                        </div>
                      </div>
                    </template>
                    
                    <div class="mb-3" v-if="!isGoogleSignIn">
                      <label class="form-label">{{ translate('auth.password', 'Password') }}</label>
                      <input 
                        type="password" 
                        class="form-control form-control-lg" 
                        :placeholder="translate('auth.password', 'Enter your password')"
                        v-model="password"
                        required
                      >
                    </div>

                    <div class="mb-3 form-check" v-if="!isGoogleSignIn && isRegistering">
                      <input type="checkbox" class="form-check-input" id="termsCheck" v-model="acceptTerms" required>
                      <label class="form-check-label" for="termsCheck">
                        {{ translate('auth.accept_terms', 'I accept') }} 
                        <a href="#" @click.prevent="openTermsModal()" class="text-primary">
                          {{ translate('auth.terms_and_conditions', 'terms and conditions') }}
                        </a>
                        {{ translate('auth.accept_terms_two', 'of the service.') }}
                        {{ translate('auth.privacy_policy', 'Our') }}
                        <a href="#" @click.prevent="openPrivacySectionModal()" class="text-primary">
                          {{ translate('auth.privacy_policy_two', 'Privacy Policy.') }} 
                        </a>
                      </label>
                    </div>
                    
                    <div class="mb-3 form-check" v-if="!isGoogleSignIn && isRegistering">
                      <input type="checkbox" class="form-check-input" id="messageCheck" v-model="acceptMessages">
                      <label class="form-check-label" for="messageCheck">
                        {{ translate('auth.accept_messages', 'I consent to receive messages at the phone numbers I provided during registration for testing.') }}
                      </label>
                    </div>

                    <div class="alert alert-danger" v-if="error">
                      {{ error }}
                    </div>

                    <div class="d-grid gap-2 mb-4">
                      <button 
                        type="submit" 
                        class="btn btn-primary btn-lg"
                        v-if="!isGoogleSignIn"
                        :disabled="loading || (isRegistering &&  (acceptTerms === false || acceptMessages === false))"
                      >
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                        {{ isRegistering ? translate('auth.register', 'Register') : translate('auth.sign_in', 'Sign In') }}
                      </button>
                      
                      <!-- Forgot Password link (only shown in login mode) -->
                      <div v-if="!isRegistering" class="text-center mt-2">
                        <a href="#" @click.prevent="showResetPasswordModal = true" class="text-primary fw-bold small">
                          {{ translate('auth.forgot_password', 'Forgot Password?') }}
                        </a>
                      </div>
                      
                      <button 
                        type="button" 
                        class="btn btn-light btn-lg d-flex align-items-center justify-content-center gap-2"
                        @click="signInWithGoogle"
                        :disabled="loading || (isRegistering &&  (acceptTerms === false || acceptMessages === false))"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285f4"/>
                          <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34a853"/>
                          <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.548 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#fbbc05"/>
                          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#ea4335"/>
                        </svg>
                        {{ translate('auth.continue_with_google', 'Continue with Google') }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Reset Password Modal -->
    <ResetPasswordModal
      v-model="showResetPasswordModal"
      :default-email="email"
      :logout-after-send="false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { setTawkAttributes } from '@/utils/tawk'
import ResetPasswordModal from '@/components/ResetPasswordModal.vue'
import TermsAndConditionsModal from '@/components/TermsAndConditionsModal.vue'

export default {
  name: 'LoginView',
  components: {
    ResetPasswordModal,
    TermsAndConditionsModal
  },
  mounted() {
    // Set tawk.to attributes for the 'login' view
    setTawkAttributes('login')
  },
  data() {
    return {
      email: '',
      password: '',
      storeName: '',
      phoneNumber: '',
      language: 'en',
      isRegistering: false,
      isGoogleSignIn: false,
      loading: false,
      error: null,
      googleDetailsLoading: false,
      registrationLoading: false,
      showGoogleModal: false,
      showRegistrationModal: false,
      showResetPasswordModal: false,
      showTermsAndConditionsModal: false,
      showPrivacySection: false,
      acceptTerms: false,
      acceptMessages: false,
      googleUserDetails: {
        storeName: '',
        phoneNumber: '',
        language: 'en',
        uid: null,
        email: null
      },
      registrationDetails: {
        storeName: '',
        phoneNumber: '',
        language: 'en'
      },
      userId: null
    }
  },
  computed: {
    ...mapState(['loading', 'error'])
  },
  methods: {
    async handleSubmit() {
      try {
        this.loading = true
        this.error = null
        
        if (this.isRegistering) {
          const result = await this.$store.dispatch('createUserWithEmailPassword', {
            email: this.email,
            password: this.password
          })
          
          if (result && result.user) {
            this.userId = result.user.uid
            
            this.showRegistrationModal = true
          } else {
            this.error = 'Registration failed. Please try again.'
          }
        } else {
          await this.$store.dispatch('loginUser', {
            email: this.email,
            password: this.password
          })
          
          this.$router.push({ name: 'Home' })
        }
      } catch (error) {
        console.error('Authentication error:', error)
        this.error = error.message || 'Authentication failed'
      } finally {
        this.loading = false
      }
    },
    
    async signInWithGoogle() {
      try {
        const result = await this.$store.dispatch('loginWithGoogle')
        if (result && result.user) {
          const user = result.user
          this.googleUserDetails.uid = user.uid
          this.googleUserDetails.email = user.email || ''
          
          if (result.hasCompleteProfile) {
            this.$router.push({ name: 'Home' })
          } else {
            this.showGoogleModal = true
          }
        } else {
          console.error('No user returned from Google sign-in')
          this.$store.commit('setError', 'Failed to authenticate with Google')
        }
      } catch (error) {
        this.$store.commit('setError', error.message || 'Authentication failed')
      }
    },
    
    showGoogleDetailsModal() {
      this.showGoogleModal = true
    },
    
    hideGoogleModal() {
      this.showGoogleModal = false
    },
    
    hideRegistrationModal() {
      this.showRegistrationModal = false
    },
    
    async submitGoogleUserDetails() {
      try {
        this.googleDetailsLoading = true
        await this.$store.dispatch('registerUserDetails', {
          storeName: this.googleUserDetails.storeName,
          phoneNumber: this.googleUserDetails.phoneNumber,
          language: this.googleUserDetails.language
        })
      
        this.hideGoogleModal()
        this.$router.push({ name: 'Home' })
      } catch (error) {
        console.error('Error saving profile:', error)
        this.$store.commit('setError', error.message || 'Failed to save profile')
      } finally {
        this.googleDetailsLoading = false
      }
    },
    
    async submitRegistrationDetails() {
      try {
        this.registrationLoading = true
        
        await this.$store.dispatch('registerUserDetails', {
          storeName: this.registrationDetails.storeName,
          phoneNumber: this.registrationDetails.phoneNumber,
          language: this.registrationDetails.language
        })
        
        this.hideRegistrationModal()
        this.$router.push({ name: 'Home' })
      } catch (error) {
        console.error('Error saving profile:', error)
        this.$store.commit('setError', error.message || 'Failed to save profile')
      } finally {
        this.registrationLoading = false
      }
    },
    
    translate(key, fallback) {
      return this.$store.getters.translate(key, fallback)
    },
    
    acceptAndCloseTerms() {
      this.acceptTerms = true;
      this.showTermsAndConditionsModal = false;
    },
    
    // Open terms modal
    openTermsModal() {
      this.showTermsAndConditionsModal = true;
      this.showPrivacySection = false;
    },
    
    // Open privacy modal
    openPrivacySectionModal() {
      this.showTermsAndConditionsModal = true;
      this.showPrivacySection = true;
    }
  }
}
</script>

<style scoped lang="scss">
.login-page {
  background-color: #f8f9fa;
}

.card {
  border: none;
  border-radius: 15px;
  overflow: hidden;
}

.btn-primary {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
}

.btn-primary:hover {
  background-color: #0056b3; /* a blue more dark as an alternative to darken() */
  border-color: #0056b3;
}

.btn-light {
  border: 1px solid #ddd;
}

.auth-tabs {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.tab-button {
  flex: 1;
  padding: 12px 15px;
  border: none;
  background: #f8f9fa;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  color: #6c757d;
}

.tab-button.active {
  background-color: var(--primary-blue);
  color: white;
}

.social-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 5px;
  margin-bottom: 10px;
  color: #212529;
  text-decoration: none;
  border: 1px solid #ddd;
  transition: background-color 0.3s, transform 0.3s;
}

.social-btn:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px);
}

.modal-backdrop {
  opacity: 0.5;
}

.form-control:focus {
  border-color: var(--primary-orange);
  box-shadow: 0 0 0 0.25rem rgba(245, 143, 35, 0.25);
}

.language-options {
  display: flex;
  gap: 10px;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  cursor: pointer;
  
  &.selected {
    background-color: var(--primary-blue);
    color: white;
  }
}

.language-flag {
  font-size: 20px;
}

.language-name {
  font-size: 16px;
}

p {
  font-size: 1.25rem;
}
</style>
